import { icon } from '../../icons';
import { i18nService, t as translate, translateDocumentType } from '../../services/i18n';
import type { RentalAgreementDto, RentalPaymentMonthDto, TenantDto } from '../../services/api';

export function getDocumentTypeLabel(type: string): string {
  return translateDocumentType(type);
}

export const DOC_TYPES = ['Epc', 'Inspection', 'Insurance', 'Invoice', 'Photo', 'Other'];

export const TENANT_ICONS = {
  chevron: icon('chevron-right', { size: 14, strokeWidth: 2.5 }),
  plus: icon('plus', { size: 16, strokeWidth: 2.5 }),
  plusSm: icon('plus', { size: 14, strokeWidth: 2.5 }),
  userLg: icon('user', { size: 48, strokeWidth: 1.5, attrs: 'opacity="0.4"' }),
  calendar: icon('calendar', { size: 13 }),
  file: icon('file', { size: 13 }),
  edit: icon('edit', { size: 15, attrs: 'stroke-linecap="round" stroke-linejoin="round"' }),
  link: icon('check-circle', { size: 14, attrs: 'stroke-linecap="round" stroke-linejoin="round"' }),
  trend: icon('trend-up', { size: 15, attrs: 'stroke-linecap="round" stroke-linejoin="round"' }),
  close: icon('x', { size: 20 }),
  dossier: icon('file', { size: 14, strokeWidth: 2.2 }),
  home: icon('home', { size: 14, strokeWidth: 2.1 }),
  moreHoriz: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.75"/><circle cx="12" cy="12" r="1.75"/><circle cx="19" cy="12" r="1.75"/></svg>',
};

export function escapeHtml(value: string): string {
  const el = document.createElement('span');
  el.textContent = value;
  return el.innerHTML;
}

export function formatDate(value?: string): string {
  if (!value) return '-';
  return i18nService.formatDate(value);
}

export function formatMonthLabel(year: number, month: number): string {
  return i18nService.formatDate(new Date(year, month - 1, 1), { month: 'short', year: 'numeric' });
}

export function formatMonthTitle(year: number, month: number): string {
  return i18nService.formatDate(new Date(year, month - 1, 1), { month: 'long', year: 'numeric' });
}

export function getRentPaymentStatusLabel(status?: string): string {
  switch (status) {
    case 'Paid':
      return translate('tenantPayments.status.paid');
    case 'Unpaid':
      return translate('tenantPayments.status.unpaid');
    default:
      return translate('tenantPayments.status.unmarked');
  }
}

export function getRentPaymentStatusClass(status?: string): string {
  switch (status) {
    case 'Paid':
      return 'rent-payment-cell--paid';
    case 'Unpaid':
      return 'rent-payment-cell--unpaid';
    default:
      return 'rent-payment-cell--empty';
  }
}

export function getMonthIndex(year: number, month: number): number {
  return (year * 12) + month;
}

export function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const date = new Date(year, month - 1 + delta, 1);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

export function getAgreementStartMonth(agreement: RentalAgreementDto): { year: number; month: number } | null {
  if (!agreement.startDate) return null;
  const date = new Date(agreement.startDate);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

export function getAgreementEndMonth(agreement: RentalAgreementDto): { year: number; month: number } {
  const today = new Date();
  const current = { year: today.getFullYear(), month: today.getMonth() + 1 };
  if (!agreement.endDate) {
    return current;
  }

  const endDate = new Date(agreement.endDate);
  const agreementEnd = { year: endDate.getFullYear(), month: endDate.getMonth() + 1 };
  return getMonthIndex(agreementEnd.year, agreementEnd.month) < getMonthIndex(current.year, current.month)
    ? agreementEnd
    : current;
}

export function getLatestSurveyEligibleYear(agreements: RentalAgreementDto[], today = new Date()): number | null {
  let latestYear: number | null = null;

  agreements.forEach((agreement) => {
    if (!agreement.startDate) {
      return;
    }

    const startDate = new Date(agreement.startDate);
    if (startDate > today) {
      return;
    }

    const endDate = agreement.endDate ? new Date(agreement.endDate) : today;
    const effectiveEnd = endDate < today ? endDate : today;
    if (effectiveEnd < startDate) {
      return;
    }

    latestYear = latestYear == null ? effectiveEnd.getFullYear() : Math.max(latestYear, effectiveEnd.getFullYear());
  });

  return latestYear;
}

export function hasOlderPaymentHistory(agreement: RentalAgreementDto, history: RentalPaymentMonthDto[]): boolean {
  if (!history.length) return false;
  const start = getAgreementStartMonth(agreement);
  if (!start) return false;

  const first = history[0];
  return getMonthIndex(first.year, first.month) > getMonthIndex(start.year, start.month);
}

export function hasNewerPaymentHistory(agreement: RentalAgreementDto, history: RentalPaymentMonthDto[]): boolean {
  if (!history.length) return false;
  const end = getAgreementEndMonth(agreement);
  const last = history[history.length - 1];
  return getMonthIndex(last.year, last.month) < getMonthIndex(end.year, end.month);
}

export function isTenantActive(tenant: TenantDto): boolean {
  if (!tenant.endDate) return true;
  return new Date(tenant.endDate) >= new Date();
}

export function isAgreementActive(agreement: RentalAgreementDto): boolean {
  return !agreement.endDate || new Date(agreement.endDate) >= new Date();
}

export function hasCurrentAgreement(agreements: RentalAgreementDto[], today = new Date()): boolean {
  return agreements.some((agreement) => {
    if (!agreement.startDate) {
      return false;
    }

    const startDate = new Date(agreement.startDate);
    if (startDate > today) {
      return false;
    }

    return !agreement.endDate || new Date(agreement.endDate) >= today;
  });
}

export function formatAgreementPeriod(agreement: RentalAgreementDto): string {
  const start = agreement.startDate ? formatDate(agreement.startDate) : '?';
  const end = agreement.endDate ? formatDate(agreement.endDate) : translate('tenant.form.currentlyRunning');
  return `${start} - ${end}`;
}
